import React, { useMemo } from 'react';
import { Box, Text, Stack, Group, Avatar } from '@mantine/core';
import { useLocaleDateFormatter } from '../../hooks/useLocaleDateFormatter';
import type { TravelerView } from '../../types';
import { parseDateValue } from '../../utils/formatDateWithLocale';

export interface CombinedTimelineProps {
  travelers: TravelerView[];
  derivedStartDate: string | null;
  derivedEndDate: string | null;
}

interface TimelineSegment {
  city: string;
  leftPct: number;
  widthPct: number;
}

function computeSegments(
  visits: Array<{ startDate?: string; endDate?: string; city: string }>,
  rangeStartMs: number,
  rangeEndMs: number
): TimelineSegment[] {
  const span = rangeEndMs - rangeStartMs;
  if (span <= 0) {
    return [];
  }

  return visits
    .map(visit => {
      const start = parseDateValue(visit.startDate);
      const end = parseDateValue(visit.endDate);
      if (!start || !end) {
        return null;
      }
      const startMs = start.getTime();
      const endMs = end.getTime();
      const leftPct = ((startMs - rangeStartMs) / span) * 100;
      const widthPct = ((endMs - startMs) / span) * 100;
      return {
        city: visit.city,
        leftPct: Math.max(0, leftPct),
        widthPct: Math.max(1, widthPct),
      };
    })
    .filter((s): s is TimelineSegment => s !== null);
}

export const CombinedTimeline: React.FC<CombinedTimelineProps> = ({
  travelers,
  derivedStartDate,
  derivedEndDate,
}) => {
  const { formatDisplayDate } = useLocaleDateFormatter();
  const timelineRange = useMemo(() => {
    const rangeStart = parseDateValue(derivedStartDate ?? undefined);
    const rangeEnd = parseDateValue(derivedEndDate ?? undefined);
    if (!rangeStart || !rangeEnd) {
      return null;
    }
    const rangeStartMs = rangeStart.getTime();
    const rangeEndMs = rangeEnd.getTime();
    if (rangeEndMs <= rangeStartMs) {
      return null;
    }
    return { rangeStart, rangeEnd, rangeStartMs, rangeEndMs };
  }, [derivedStartDate, derivedEndDate]);

  const rows = useMemo(() => {
    if (!timelineRange) {
      return [];
    }
    const { rangeStartMs, rangeEndMs } = timelineRange;

    return travelers
      .map(traveler => {
        const visitsWithDates = (traveler.membership.visits ?? [])
          .filter(v => v.startDate && v.endDate)
          .map(v => ({
            startDate: v.startDate,
            endDate: v.endDate,
            city: v.location.city,
          }));

        if (visitsWithDates.length === 0) {
          return null;
        }

        return {
          traveler,
          segments: computeSegments(visitsWithDates, rangeStartMs, rangeEndMs),
        };
      })
      .filter((row): row is NonNullable<typeof row> => row !== null);
  }, [travelers, timelineRange]);

  if (!timelineRange || rows.length === 0) {
    return (
      <Text size="sm" c="dimmed" data-cy="combined-timeline">
        Add dated membership visits to see the combined timeline.
      </Text>
    );
  }

  const { rangeStart, rangeEnd } = timelineRange;

  return (
    <Stack gap="md" data-cy="combined-timeline">
      <Box
        data-cy="combined-timeline-group"
        p="sm"
        style={{
          borderRadius: 8,
          border: '1px solid var(--mantine-color-slate-4)',
        }}
      >
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            columnGap: 'var(--mantine-spacing-md)',
            rowGap: 'var(--mantine-spacing-md)',
            alignItems: 'center',
          }}
        >
          {rows.map(({ traveler, segments }) => (
            <React.Fragment key={traveler.membership.id}>
              <Group
                gap="xs"
                wrap="nowrap"
                data-cy={`timeline-traveler-row-${traveler.membership.traveler.id}`}
              >
                <Avatar
                  size="sm"
                  radius="xl"
                  style={{ backgroundColor: traveler.color }}
                >
                  {traveler.membership.traveler.name.charAt(0)}
                </Avatar>
                <Text size="sm" fw={500}>
                  {traveler.membership.traveler.name}
                </Text>
              </Group>
              <Box
                data-cy={`timeline-traveler-bar-${traveler.membership.traveler.id}`}
                style={{
                  position: 'relative',
                  height: 36,
                  backgroundColor: 'var(--mantine-color-slate-7)',
                  borderRadius: 8,
                }}
              >
                {segments.map((seg, idx) => (
                  <Box
                    key={`${seg.city}-${idx}`}
                    style={{
                      position: 'absolute',
                      left: `${seg.leftPct}%`,
                      width: `${seg.widthPct}%`,
                      top: 4,
                      bottom: 4,
                      backgroundColor: traveler.color,
                      border: '1px solid var(--mantine-color-white)',
                      borderRadius: 6,
                      boxSizing: 'border-box',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      minWidth: 24,
                    }}
                  >
                    <Text size="xs" c="white" truncate>
                      {seg.city}
                    </Text>
                  </Box>
                ))}
              </Box>
            </React.Fragment>
          ))}
          <Box aria-hidden />
          <Group
            justify="space-between"
            gap="xs"
            data-cy="timeline-range-dates"
          >
            <Text size="xs" c="dimmed">
              {formatDisplayDate(rangeStart)}
            </Text>
            <Text size="xs" c="dimmed">
              {formatDisplayDate(rangeEnd)}
            </Text>
          </Group>
        </Box>
      </Box>
    </Stack>
  );
};

export default CombinedTimeline;
